import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { BANDWIDTH, RESPONSE_TIME, RESPONSES_CODES, TRAFIC } from '@shared/components/highcharts/graph/graph';
import { AuthService } from '@shared/services/auth.service';
import { SitesService } from '@shared/services/sites/sites.service';
import { MY_LOGS_ROUTE } from 'app/app-routing.constants';
import { formatHumanReadableDate } from '@shared/utils/data-utils';
import { DATE_NOW, RANGE_PRESETS } from '@shared/services/calendar-data.service';
import { CountryNames, CountryService } from '@shared/services/country.service';
import _ from 'lodash';
import { FiltersEnum } from '../my-logs/filters/filters';
import { DateRange } from '@shared/utils/date-range';
import {
  EventSeverity,
  EventSubTypeOption,
  EventType,
  EventTypes,
  JournalEvent,
  JournalQuery,
  OrganizationJournalService,
} from '@shared/services/organization-journal.service';
import { ClusterUidAndName } from '@shared/models/cluster';
import { CONTEXT, ContextEnum, JournalFilters } from '@shared/models/ContextSiteModel';
import { SiteSelectorComponent } from '@shared/components/site-selector/site-selector.component';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { FormsModule } from '@angular/forms';
import { DecimalPipe, LowerCasePipe, NgClass, NgFor, NgIf, NgStyle, TitleCasePipe } from '@angular/common';
import { DateRangeSelectorDirective } from '@shared/directives/date-range-selector.directive';
import { HelpComponent } from '@shared/components/help/help.component';
import { NgxDatatableModule } from '@siemens/ngx-datatable';
import { TypedTemplateDirective } from '@shared/directives/typed-template.directive';
import { JsonViewer } from '@shared/components/json-viewer/json-viewer.component';
import { DateFormatPipe } from '@shared/pipe/filter.pipe';
import * as pager from '@shared/pager';

interface BrainDrive {
  driveUuid: string;
  uid?: string;
  label: string;
}

@Component({
  selector: 'app-journal',
  templateUrl: './journal.component.html',
  styleUrls: ['./journal.component.scss'],
  imports: [
    SiteSelectorComponent,
    NgSelectComponent,
    FormsModule,
    NgFor,
    NgOptionComponent,
    DateRangeSelectorDirective,
    HelpComponent,
    NgxDatatableModule,
    TypedTemplateDirective,
    JsonViewer,
    NgIf,
    NgClass,
    NgStyle,
    LowerCasePipe,
    DecimalPipe,
    TitleCasePipe,
    DateFormatPipe,
    TranslatePipe,
  ],
})
export class JournalComponent implements OnInit, OnDestroy {
  lang: string;
  @ViewChild('logsTable') logsTable;

  ctx: ContextEnum = CONTEXT;

  mapGraphTypeTrack = {
    newIpBlocked: TRAFIC,
    highBannedVolume: TRAFIC,
    increasedResponseTime: RESPONSE_TIME,
    increasedBandwidth: BANDWIDTH,
    '4XX': RESPONSES_CODES,
    '5XX': RESPONSES_CODES,
    traficSpike: TRAFIC,
    increased4xx5xxRate: RESPONSES_CODES,
    increased4xxRate: RESPONSES_CODES,
    increased5xxRate: RESPONSES_CODES,
    increasedOriginError: RESPONSES_CODES,
  };

  logs: JournalEventVM[] = [];
  highlighted: Set<number> = new Set<number>();
  eventSubTypes: EventSubTypeOption[] = [];
  brainDrives: { [prop: string]: BrainDrive } = {};
  isLoading: boolean = false;
  previouslySelectedTypes: EventType[] = [];
  total = 0;
  size = 25;

  countries: CountryNames;

  index = 0;
  filters: JournalFilters = {
    period: {
      range: RANGE_PRESETS['T'],
      autoRefresh: false,
    },
    type: [],
    subType: [],
  };

  rowType = { row: null as JournalEventVM, expanded: false };

  constructor(
    private journalService: OrganizationJournalService,
    private http: HttpClient,
    private auth: AuthService,
    public translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private sites: SitesService,
    private countryService: CountryService,
  ) {}

  get availableSubTypes(): EventSubTypeOption[] {
    const types = this.filters?.type;
    if (!types?.length) return this.eventSubTypes;
    return this.eventSubTypes.filter((st) => types.includes(st.type));
  }

  async ngOnInit() {
    this.lang = this.auth.getCurrentLanguage();
    await this.sites.load(this.ctx);
    this.route.queryParams.subscribe((params) => {
      // If data is passed from query-string
      if (Object.keys(params).length) {
        if (params['before']) {
          this.filters.period = {
            range: new DateRange(
              moment(params['before']).subtract(30, 'seconds'),
              moment(params['before']).add(1, 'milliseconds'),
            ),
            autoRefresh: false,
          };
        }

        if (params['severity']) {
          this.filters.severity = params['severity'];
        }

        if (params['site']) {
          this.sites[this.ctx].selectSites([params['site']]);
        }

        if (params['clusterId']) {
          this.sites[this.ctx].selectClusterByClusterUid(params['clusterId']);
        }
        if (params['cluster']) {
          this.sites[this.ctx].selectClusterByClusterUid(params['cluster']);
        }

        if (params['type']) {
          this.filters.type = params['type'];
        }

        if (params['id']) {
          this.highlighted.add(Number.parseInt(params['id']));
        }
      } else {
        // else if data is stored in context from previous navigation
        if (this.sites[this.ctx].current.journalFilters) {
          this.filters = this.sites[this.ctx].current.journalFilters;
          // else default
        } else {
          this.filters.period = {
            range: RANGE_PRESETS['T'],
            autoRefresh: false,
          };
        }
      }
    });

    await this.countryService.init();
    this.countries = this.countryService.getCountryNames();
    await this.getCodesAttacks();
    this.journalService.getSubTypes().subscribe((value) => (this.eventSubTypes = value));

    await this.refresh();
  }
  ngOnDestroy() {
    this.sites[this.ctx].current.journalFilters = this.filters;
    this.highlighted.clear();
  }

  refresh() {
    this.index = 0;
    this.filters.period.range = this.filters.period.range.refresh();
    this.getEvents();
  }

  onPeriodEmitted(p: DateRange) {
    this.filters.period = {
      range: p,
      autoRefresh: false,
    };
    this.refresh();
  }

  toggleExpandRow(row) {
    this.logsTable.rowDetail.toggleExpandRow(row);
  }

  changePage(e: any) {
    this.index = e.page - 1;
    this.getEvents();
  }

  async getCodesAttacks() {
    this.brainDrives = _.keyBy(
      await firstValueFrom(this.http.get<BrainDrive[]>('v2/brain-drives')),
      (drive) => drive.uid,
    );
  }

  getSearchParameters(): Partial<JournalQuery> {
    return {
      after: this.filters?.period?.range?.start,
      before: this.filters.period?.range?.end,
      cluster: this.sites[this.ctx]?.current?.cluster?.uid,
      sites: this.sites[this.ctx]?.current?.selectedSites.map((s) => s.domainName),
      tags: this.sites[this.ctx]?.current?.tag ? [this.sites[this.ctx]?.current?.tag] : null,
      severity: this.filters?.severity,
      type: this.filters?.type?.length > 0 ? this.filters?.type : this.EventTypes,
      subtype:
        this.filters.subType?.length > 0
          ? this.filters.subType.map((o) => o.code)
          : this.availableSubTypes.map((r) => r.code),
      ip: this.filters?.ip,
    };
  }

  async getEvents() {
    this.isLoading = true;
    const params = this.getSearchParameters();
    try {
      const res = await firstValueFrom(
        this.journalService.getJournalEvents(params, { page: this.index, size: this.size }),
      );
      this.total = res.totalElements;
      this.logs = res.content.map((log) => this.formatEvent(log));
    } finally {
      this.isLoading = false;
    }
  }

  formatEvent(event: JournalEvent) {
    let formattedEvent: JournalEventVM = {
      ...event,
      outOfDate: moment().diff(event.date ?? 0, 'days') > 89,
      cluster: this.sites[this.ctx].store.clusters.find((c) => c.uid == event.clusterUid),
    };

    if (event.event.drives) {
      event.event.drives.forEach((drive) => {
        drive.label = this.brainDrives[drive.uid]?.label || 'DRIVE_NOT_FOUND: ' + drive.uid;
        delete drive.uid;
      });
    }

    ['firstSuspicion', 'block', 'startDate', 'endDate'].forEach((field) => {
      if (event.event[field]) {
        event.event[field] = formatHumanReadableDate(event.event[field], this.lang, 'long');
      }
    });

    return formattedEvent;
  }

  trackEvent(row) {
    let end = moment(row.date).add(30, 'minutes');
    const now = DATE_NOW();

    if (end.isAfter(now)) {
      end = now;
    }

    this.sites[this.ctx].current.period = {
      range: new DateRange(moment(row.date).subtract(30, 'minutes'), end),
      autoRefresh: false,
    };

    let query = {
      state: {
        graphType: this.mapGraphTypeTrack[row.subtype],
        sites: [row.site],
        clusterName: row.cluster.name,
      },
    };

    if (row.event.ip) {
      query.state[FiltersEnum.IP] = row.event.ip;
    }

    this.router.navigate([MY_LOGS_ROUTE], query);
  }

  getRowClass = (row: JournalEvent) => ({
    'row-highlight': this.highlighted.has(row.id),
  });

  protected readonly EventSeverity = EventSeverity;
  protected readonly EventTypes = EventTypes;

  protected readonly CLICKABLE_TYPES: EventType[] = ['SECURITY', 'MONITORING'];
  protected readonly NON_CLICKABLE_SUBTYPES = ['siteRateLimit'];
  protected readonly pager = pager;
}

type JournalEventVM = JournalEvent & {
  cluster?: ClusterUidAndName;
  outOfDate: boolean;
};
